// Single source of truth for schema validation + ownership.
// Pure functions only — no AWS SDK, no native, no nanoid. Imported by the
// Lambda (bundled via fromAsset) AND by the RN app (via src/backend/rules.ts).

export interface RuleValidation {
  type: 'required' | 'min' | 'max' | 'minLength' | 'maxLength' | 'pattern' | 'custom';
  value?: unknown;
  message?: string;
}

export interface RuleField {
  name: string;
  type: string;
  validation?: RuleValidation[];
}

export interface RuleSchema {
  name: string;
  fields: RuleField[];
  ownership?: 'public' | 'user' | 'role' | 'ownerWrite';
  ownerField?: string;
}

export interface RuleUser {
  userId: string;
  /** Present on a real authenticated caller; used to tell an owner WHO submitted a row. */
  email?: string;
}

/**
 * The field an owner reads to see who sent something.
 *
 * Server-written only. A visitor signed in with their Saddle account is somebody the owner
 * should be able to identify — that is the point of asking them to sign in — but the client
 * must never be able to claim it, or a form post could name anybody.
 */
export const SUBMITTED_BY = 'submittedBy';

export type AccessAction = 'read' | 'create' | 'update' | 'delete';

/** Per-app role of the requesting user (resolved by the handler from enrollments).
 *  'admin' = an end-user the creator promotes to manage users/data (sees all rows)
 *  but not edit the app definition. 'user' = default end-user (own rows only);
 *  'member' is a back-compat alias of 'user'. 'blocked' = access revoked (deny all;
 *  sticks across re-use — auto-enroll won't resurrect it). */
export type AppRole = 'owner' | 'editor' | 'admin' | 'user' | 'member' | 'blocked';

export interface AccessContext {
  /** The caller's role for THIS app, resolved server-side. Absent → not a member. */
  appRole?: AppRole;
}

/**
 * `allowUnknown` exists for collections whose shape is the OWNER's to decide.
 *
 * Refusing an undeclared key is the right default: a public form's fields are fixed when
 * the app is built, and anything else arriving is either a mistake or an attempt. But an
 * owner-managed collection — a rota, a timetable — is content, and its columns change when
 * the owner decides they should. Declaring a fixed field list there would mean the server
 * rejecting the very column the owner just added.
 */
export function validateRequestBody(
  data: Record<string, unknown>,
  fields: RuleField[],
  options: { allowUnknown?: boolean } = {},
): string[] {
  const errors: string[] = [];
  const fieldMap = new Map(fields.map((f) => [f.name, f]));

  if (!options.allowUnknown) {
    for (const key of Object.keys(data)) {
      if (key === 'id') continue;
      if (!fieldMap.has(key)) errors.push(`Unknown field: ${key}`);
    }
  }

  for (const field of fields) {
    if (!field.validation) continue;
    for (const rule of field.validation) {
      const err = validateField(field.name, data[field.name], rule);
      if (err) errors.push(err);
    }
  }
  return errors;
}

export function validateField(name: string, value: unknown, rule: RuleValidation): string | null {
  const msg = rule.message ?? `Validation failed for ${name}`;
  switch (rule.type) {
    case 'required':
      if (value === undefined || value === null || value === '') return msg;
      break;
    case 'min':
      if (typeof value === 'number' && value < (rule.value as number)) return msg;
      break;
    case 'max':
      if (typeof value === 'number' && value > (rule.value as number)) return msg;
      break;
    case 'minLength':
      if (typeof value === 'string' && value.length < (rule.value as number)) return msg;
      break;
    case 'maxLength':
      if (typeof value === 'string' && value.length > (rule.value as number)) return msg;
      break;
    case 'pattern':
      if (typeof value === 'string' && !new RegExp(rule.value as string).test(value)) return msg;
      break;
  }
  return null;
}

export function checkAccess(
  schema: RuleSchema,
  action: AccessAction,
  user: RuleUser | null,
  record?: Record<string, unknown>,
  ctx?: AccessContext,
): { allowed: boolean; reason?: string } {
  const ownership = schema.ownership ?? 'public';

  switch (ownership) {
    case 'public':
      if (action === 'read') return { allowed: true };
      if (!user) return { allowed: false, reason: 'Authentication required' };
      if (action === 'create') return { allowed: true };
      if (record && schema.ownerField && record[schema.ownerField] !== user.userId) {
        return { allowed: false, reason: 'Not the owner of this record' };
      }
      return { allowed: true };

    /**
     * Owner-managed content that visitors read: a rota, a timetable, a class list.
     *
     * Distinct from 'public' because 'public' lets ANY signed-in person write, which is
     * right for a form and wrong for a schedule — and distinct from 'role' because that
     * requires a reader to be signed in, which a published page's visitors are not.
     * Reading is open to everyone, including anonymously; writing is the owner's alone.
     */
    case 'ownerWrite': {
      if (action === 'read') return { allowed: true };
      if (!user) return { allowed: false, reason: 'Authentication required' };
      const role = ctx?.appRole;
      if (role === 'owner' || role === 'editor' || role === 'admin') return { allowed: true };
      return { allowed: false, reason: 'Only the owner of this app can change this' };
    }

    case 'user':
      if (!user) return { allowed: false, reason: 'Authentication required' };
      if (action === 'create') return { allowed: true };
      if (record && schema.ownerField && record[schema.ownerField] !== user.userId) {
        return { allowed: false, reason: 'Access denied' };
      }
      return { allowed: true };

    case 'role': {
      // Role-scoped data: gated by the caller's per-app role (owner/editor/member).
      // Deny-by-default — no authenticated user or no app role → no access. (This
      // replaces the old stub that allowed any authenticated user.)
      if (!user) return { allowed: false, reason: 'Authentication required' };
      const appRole = ctx?.appRole;
      if (!appRole) return { allowed: false, reason: 'Not a member of this app' };
      if (appRole === 'blocked') return { allowed: false, reason: 'Access to this app has been revoked' };
      // Create their own rows (owner-stamped server-side).
      if (action === 'create') return { allowed: true };
      // Owner/editor/admin may read+modify ANY record.
      if (appRole === 'owner' || appRole === 'editor' || appRole === 'admin') return { allowed: true };
      // user/member may read+modify only their OWN records. (A bare list read has
      // no record here → allowed, then the handler filters the list to own rows.)
      if (record && schema.ownerField && record[schema.ownerField] !== user.userId) {
        return { allowed: false, reason: 'You can only access your own records' };
      }
      return { allowed: true };
    }

    default:
      return { allowed: true };
  }
}

export function addOwnership(
  schema: RuleSchema,
  data: Record<string, unknown>,
  user: RuleUser | null,
): Record<string, unknown> {
  // Whoever the client says sent this is discarded before anything else. The only source
  // for it is the verified token.
  const clean = { ...data };
  delete clean[SUBMITTED_BY];

  /*
   * A signed-in visitor is somebody the owner can see.
   *
   * A submission used to be anonymous whether or not the person was signed in, so an owner
   * looking at their list could not tell who any of it came from beyond what the form
   * happened to ask. Now that one Saddle account works across every app, signing in is the
   * normal case — and the reason to ask somebody to sign in at all is that it tells the
   * owner who they are.
   */
  const withSender = user?.email ? { ...clean, [SUBMITTED_BY]: user.email } : clean;

  // Always stamp the owner to the authenticated user on create — never trust a
  // client-supplied owner field (prevents spoofing another user's ownership).
  if (user && schema.ownerField) {
    return { ...withSender, [schema.ownerField]: user.userId };
  }
  return withSender;
}
